import { el, clear } from "../util/dom";
import { sendBugReport } from "../api/endpoints";
import { recentClientErrors } from "../clientErrors";
import { isNormalized } from "../displayMode";
import { formatLastSeen } from "../util/format";
import { checkEntitlement, deleteAccount, deviceLimitInfo, isLoggedIn, isThisDevice, listDevices, login, logout, requestPasswordReset, revokeDevice, revokeOtherDevices, submitUpsellInterest, type DeviceLimitInfo, type EntitlementStatus } from "../api/auth";
import { buildPasswordField } from "./passwordField";
import { cancelSubscription, createCheckoutSession } from "../api/billing";
import { pingFunnel } from "../api/funnel";
import { ApiError } from "../api/client";
import { CHECKOUT_ENABLED } from "../config";

export interface AuthView {
  root: HTMLElement;
  refresh(): Promise<EntitlementStatus>;
  onAuthChange(cb: (loggedIn: boolean) => void): void;
}

export function buildAuthView(onError: (msg: string) => void): AuthView {
  const container = el("div", { class: "auth-panel" });
  // A persistent notice element ABOVE the (repeatedly-cleared) container, so a
  // Stripe-return message survives the render swaps below.
  const noticeEl = el("div", { class: "checkout-notice" });
  noticeEl.style.display = "none";
  const root = el("div", {}, [noticeEl, container]);

  let authChangeCb: (loggedIn: boolean) => void = () => {};

  // B-005 handoff: the marketing pricing page sends a not-yet-logged-in
  // visitor here as `app.bitclock.net/?next=subscribe`. After they log in (or
  // if already logged in) and we confirm they're on the free tier, we take
  // them straight into Stripe checkout -- one continuous flow from "Subscribe"
  // on bitclock.net to Stripe, with the dashboard login bridging the gap.
  const params = new URLSearchParams(window.location.search);
  const checkoutResult = params.get("checkout");
  let pendingSubscribe = params.get("next") === "subscribe";

  // Handle the return from Stripe (success/cancel URLs the API is configured
  // to send the browser back to).
  if (checkoutResult === "success") {
    showNotice(
      "Payment received -- thank you! Your subscription is being activated; full access unlocks within a moment.",
      "success"
    );
  } else if (checkoutResult === "cancelled" || checkoutResult === "canceled") {
    showNotice(
      "Checkout cancelled -- no charge was made. You can subscribe any time.",
      "info"
    );
  }

  // Strip the one-shot query params so a manual refresh doesn't re-show the
  // notice or re-trigger the auto-subscribe handoff.
  if (checkoutResult || pendingSubscribe) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      url.searchParams.delete("next");
      window.history.replaceState({}, "", url.toString());
    } catch {
      /* history API unavailable -- harmless, the flags are already read */
    }
  }

  function showNotice(message: string, kind: "success" | "info"): void {
    noticeEl.textContent = message;
    noticeEl.className = `checkout-notice checkout-notice-${kind}`;
    noticeEl.style.display = "";
  }

  function describeError(err: unknown): string {
    if (err instanceof ApiError) return err.detail;
    return err instanceof Error ? err.message : String(err);
  }

  // SW-3: the device-cap 409's message + upsell capture live in this slot,
  // rebuilt with each renderLoggedOut(). It is filled ONLY by doLogin's
  // device_limit branch -- the upsell never appears anywhere else.
  let deviceLimitSlot: HTMLElement | null = null;

  function hideDeviceLimit(): void {
    if (deviceLimitSlot) {
      clear(deviceLimitSlot);
      deviceLimitSlot.style.display = "none";
    }
  }

  function showDeviceLimit(email: string, limit: DeviceLimitInfo): void {
    const slot = deviceLimitSlot;
    if (!slot) return;
    clear(slot);
    slot.style.display = "";
    slot.append(el("div", { class: "device-limit-message" }, [limit.message]));
    // SW-22: the devices holding the slots, when the API sends them.
    if (limit.devices.length > 0) {
      slot.append(
        el(
          "div",
          { class: "device-list" },
          limit.devices.map((d) =>
            el("div", { class: "device-row" }, [
              el("span", {}, [d.device_name || "Unnamed device"]),
              el("span", { class: "device-meta" }, [`last seen ${formatLastSeen(d.last_seen_utc)}`]),
            ])
          )
        )
      );
    }
    if (limit.upsell) {
      const promptEl = el("p", { class: "subscribe-status" }, [limit.upsell.prompt]);
      const yesBtn = el("button", { class: "auth-btn" }, ["I'd be interested"]);
      const noBtn = el("button", { class: "auth-btn auth-btn-secondary" }, ["No thanks"]);
      const row = el("div", { class: "auth-row" }, [yesBtn, noBtn]);
      const answer = async (response: "interested" | "not_interested"): Promise<void> => {
        row.remove();
        try {
          await submitUpsellInterest(email, response);
        } catch {
          // Best-effort interest capture -- never surface an error for it.
        }
        promptEl.textContent = "Thanks — noted.";
      };
      yesBtn.addEventListener("click", () => void answer("interested"));
      noBtn.addEventListener("click", () => void answer("not_interested"));
      slot.append(promptEl, row);
    }
  }

  function renderLoggedOut(): void {
    clear(container);
    deviceLimitSlot = el("div", { class: "device-limit" });
    deviceLimitSlot.style.display = "none";
    const emailInput = el("input", { type: "email", placeholder: "email", class: "auth-input" }) as HTMLInputElement;
    const { wrap: passwordWrap, input: passwordInput } = buildPasswordField("password (min 8 chars)");
    const loginBtn = el("button", { class: "auth-btn" }, ["Log in"]);
    // SW-24: registration is its own screen (registerView.ts) with its own
    // fields, validation and marketing opt-in; this form only links to it.
    const registerLink = el(
      "a",
      { class: "auth-btn auth-btn-secondary", href: "/register", "data-route": "" },
      ["Create an account"]
    );

    loginBtn.addEventListener("click", () => void doLogin(emailInput.value, passwordInput.value));
    passwordInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void doLogin(emailInput.value, passwordInput.value);
    });

    // "Forgot password?" link (T-098). Prompts for the email, POSTs the
    // reset request, and shows the same generic confirmation whether or
    // not the address is registered (the backend never reveals which --
    // anti-enumeration). A status line below the form carries the result.
    const forgotStatus = el("p", { class: "auth-forgot-status" });
    const forgotLink = el("a", { class: "auth-forgot-link", href: "#" }, ["Forgot password?"]);
    forgotLink.addEventListener("click", (e) => {
      e.preventDefault();
      void doForgotPassword(emailInput.value, forgotStatus);
    });

    const rows: (Node | string)[] = [];
    // The ?next=subscribe handoff only promises checkout when checkout is
    // actually enabled. While gated off (CHECKOUT_ENABLED=false) we say nothing
    // about subscribing here so the login view never advertises a flow that
    // dead-ends at "Coming soon".
    if (pendingSubscribe && CHECKOUT_ENABLED) {
      rows.push(
        el("div", { class: "subscribe-cta" }, [
          "Log in or create an account to continue to secure checkout ($26.99/mo).",
        ])
      );
    }
    rows.push(
      deviceLimitSlot,
      el("div", { class: "auth-row" }, [emailInput]),
      el("div", { class: "auth-row" }, [passwordWrap]),
      el("div", { class: "auth-row" }, [loginBtn, registerLink]),
      el("div", { class: "auth-row auth-row-forgot" }, [forgotLink]),
      forgotStatus
    );
    container.append(...rows);
  }

  function renderLoggedIn(status: EntitlementStatus): void {
    clear(container);
    const badgeClass =
      status === "subscribed" ? "badge-subscribed" : status === "free" ? "badge-free" : "badge-unknown";
    const badgeText =
      status === "subscribed"
        ? "Subscribed"
        : status === "free"
          ? "Free tier -- live ratio only (historical data requires a subscription)"
          : "Entitlement status unavailable";

    const logoutBtn = el("button", { class: "auth-btn" }, ["Log out"]);
    const deleteBtn = el("button", { class: "auth-btn auth-btn-danger" }, ["Delete account"]);

    // SW-14: one click sends a bug report with the page context and the
    // recent client errors attached; the API stamps the account and emails
    // support. No form -- the click IS the report.
    const bugBtn = el("button", { class: "auth-btn auth-btn-secondary" }, ["Report a bug"]);
    const bugStatus = el("p", { class: "subscribe-status" });
    bugBtn.addEventListener("click", () => {
      bugBtn.disabled = true;
      bugStatus.textContent = "Sending...";
      const bundle = Array.from(document.scripts).map((s) => s.src.split("/").pop() ?? "").find((n) => n.startsWith("main-")) ?? "";
      sendBugReport({
        client: "web",
        app_version: bundle,
        description: "",
        context: {
          url: location.href,
          user_agent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
          scale: isNormalized() ? "NORMALIZED" : "RAW",
          entitlement: status,
        },
        recent_errors: recentClientErrors(),
      })
        .then(() => {
          bugStatus.textContent = "Bug report sent -- thank you. We read every one.";
        })
        .catch((err: unknown) => {
          bugStatus.textContent = err instanceof Error ? `Could not send: ${err.message}` : "Could not send the report.";
          bugBtn.disabled = false;
        });
    });

    function doLogout(): void {
      logout();
      authChangeCb(false);
      renderLoggedOut();
    }
    logoutBtn.addEventListener("click", doLogout);

    // SW-22: Devices -- every device holding one of the account's slots, a
    // sign-out control per row (this device = the ordinary log out) and one
    // for all the others. Re-listed after each action.
    const devicesList = el("div", { class: "device-list" }, ["Loading devices…"]);
    const devicesStatus = el("p", { class: "subscribe-status" });
    const signOutOthersBtn = el("button", { class: "auth-btn auth-btn-secondary" }, [
      "Sign out all other devices",
    ]) as HTMLButtonElement;
    async function refreshDevices(): Promise<void> {
      try {
        const { devices, limit } = await listDevices();
        clear(devicesList);
        devicesStatus.textContent = `${devices.length} of ${limit} device slots in use.`;
        signOutOthersBtn.disabled = !devices.some((d) => !isThisDevice(d));
        for (const d of devices) {
          const mine = isThisDevice(d);
          const signOutBtn = el("button", { class: "auth-btn auth-btn-secondary" }, ["Sign out"]);
          signOutBtn.addEventListener("click", () => {
            if (mine) {
              doLogout();
              return;
            }
            if (!confirm(`Sign out ${d.device_name || "this unnamed device"}?`)) return;
            revokeDevice(d.device_id).then(refreshDevices, (err: unknown) => onError(describeError(err)));
          });
          devicesList.append(
            el("div", { class: "device-row" }, [
              el("span", {}, [
                d.device_name || "Unnamed device",
                mine ? el("span", { class: "device-current" }, ["this device"]) : null,
              ]),
              el("span", { class: "device-meta" }, [`last seen ${formatLastSeen(d.last_seen_utc)}`]),
              signOutBtn,
            ])
          );
        }
      } catch (err) {
        devicesList.textContent = "";
        devicesStatus.textContent = `Couldn't load devices: ${describeError(err)}`;
      }
    }
    signOutOthersBtn.addEventListener("click", () => {
      if (!confirm("Sign out every device except this one?")) return;
      signOutOthersBtn.disabled = true;
      revokeOtherDevices().then(refreshDevices, (err: unknown) => {
        signOutOthersBtn.disabled = false;
        onError(describeError(err));
      });
    });
    void refreshDevices();
    deleteBtn.addEventListener("click", () => void doDeleteAccount());

    container.append(el("div", { class: `entitlement-badge ${badgeClass}` }, [badgeText]));

    // Subscribe control: only for a logged-in user who is NOT already
    // entitled. A subscribed user sees the badge above and no button (the
    // brief's "Subscribed, no button" state). "unknown"
    // (probe failed) deliberately shows no Subscribe button either -- we
    // don't push someone toward paying while we can't confirm they aren't
    // already paying.
    if (status === "free") {
      if (CHECKOUT_ENABLED) {
        // WORKING CHECKOUT (dd4ecf2) — restored intact when the launch gate is
        // flipped on. A free-tier user gets the real $26.99/mo Subscribe button
        // that POSTs the checkout-session endpoint and redirects to Stripe.
        const subStatus = el("p", { class: "subscribe-status" });
        const subscribeBtn = el("button", { class: "auth-btn subscribe-btn" }, ["Subscribe -- $26.99/mo"]);
        subscribeBtn.addEventListener("click", () => void startCheckout(subscribeBtn, subStatus));
        container.append(el("div", { class: "auth-row" }, [subscribeBtn]), subStatus);

        // Secondary on-chain option (Owner-approved 2026-08-29): BTCPay hosted
        // checkout at pay.bitclock.net. The entitlement lands via the api
        // webhook (source='crypto') -- the dashboard needs no callback
        // handling; the user's entitlement check picks it up on next load.
        const btcPayLink = el(
          "a",
          {
            class: "auth-btn auth-btn-secondary",
            href: "https://pay.bitclock.net",
            target: "_blank",
            rel: "noopener",
          },
          ["Pay with Bitcoin"]
        );
        // XDIV 0c: the BTCPay tab is the other checkout surface.
        btcPayLink.addEventListener("click", () => pingFunnel("paywall_view"));
        container.append(
          el("div", { class: "auth-row" }, [btcPayLink]),
          el("p", { class: "subscribe-status" }, [
            "Monthly or prepaid terms — pay on-chain from any wallet.",
          ])
        );

        // Seamless handoff from the marketing page (?next=subscribe): now that
        // we've confirmed free-tier status, go straight to checkout. Consume
        // the flag so it fires exactly once.
        if (pendingSubscribe) {
          pendingSubscribe = false;
          void startCheckout(subscribeBtn, subStatus);
        }
      } else {
        // LAUNCH GATE (CHECKOUT_ENABLED=false) — checkout is held. Render an
        // inert "Coming soon" state that mirrors the marketing site: no working
        // button, no click handler, no checkout-session call, no Stripe
        // redirect. Consume any ?next=subscribe handoff without starting
        // checkout so it can't dead-end or silently retry.
        pendingSubscribe = false;
        container.append(
          el("div", { class: "subscribe-comingsoon" }, [
            el("span", { class: "subscribe-comingsoon-label" }, ["Coming soon"]),
            " Subscriptions open at launch. Full Access is $26.99/month — secure checkout isn't live yet.",
          ]),
          el("div", { class: "auth-row" }, [
            el(
              "span",
              {
                class: "auth-btn subscribe-btn subscribe-btn-disabled",
                role: "button",
                "aria-disabled": "true",
                tabindex: "-1",
              },
              ["Subscribe -- Coming soon"]
            ),
          ])
        );
      }
    }

    // Cancel-subscription flow with ONE optional free-text question
    // (marketing request, Owner-cleared 2026-08-29). No confirm-guilt loop:
    // the first click swaps the button for the question + the real cancel
    // button, and that second click cancels immediately whether or not
    // anything was typed. The reason rides the same cancel call as an
    // optional `reason` field (omitted when empty -- see api/billing.ts).
    if (status === "subscribed") {
      const cancelStatus = el("p", { class: "subscribe-status" });
      const cancelRow = el("div", { class: "auth-row" });
      const revealBtn = el("button", { class: "auth-btn auth-btn-secondary" }, ["Cancel subscription"]);
      revealBtn.addEventListener("click", () => {
        const reasonInput = el("textarea", {
          class: "auth-input cancel-reason-input",
          placeholder: "Anything we should know about why? (optional)",
          maxlength: "2000",
          rows: "2",
        }) as HTMLTextAreaElement;
        const confirmBtn = el("button", { class: "auth-btn auth-btn-danger" }, ["Cancel subscription"]);
        confirmBtn.addEventListener("click", () =>
          void doCancelSubscription(confirmBtn, reasonInput.value, cancelStatus)
        );
        cancelRow.replaceChildren(reasonInput, confirmBtn);
      });
      cancelRow.append(revealBtn);
      container.append(cancelRow, cancelStatus);
    }

    container.append(
      el("div", { class: "section-title" }, ["Devices"]),
      devicesList,
      el("div", { class: "auth-row" }, [signOutOthersBtn]),
      devicesStatus,
      el("div", { class: "section-title" }, ["Session"])
    );
    container.append(el("div", { class: "auth-row" }, [logoutBtn, deleteBtn]));
    container.append(el("div", { class: "auth-row" }, [bugBtn]), bugStatus);
  }

  async function doCancelSubscription(
    button: HTMLElement,
    reason: string,
    statusEl: HTMLElement
  ): Promise<void> {
    (button as HTMLButtonElement).disabled = true;
    statusEl.textContent = "Cancelling…";
    try {
      await cancelSubscription(reason);
      showNotice("Your subscription has been cancelled. You can resubscribe any time.", "info");
      // Re-read the real entitlement and re-render (Stripe may keep access
      // until the period ends, so the badge reflects whatever the API says).
      const status = await checkEntitlement();
      if (status === "loggedOut") renderLoggedOut();
      else renderLoggedIn(status);
    } catch (err) {
      statusEl.textContent = `Couldn't cancel right now: ${describeError(err)}`;
      (button as HTMLButtonElement).disabled = false;
    }
  }

  async function startCheckout(button: HTMLElement, statusEl: HTMLElement): Promise<void> {
    (button as HTMLButtonElement).disabled = true;
    statusEl.textContent = "Starting secure checkout…";
    try {
      const { checkout_url } = await createCheckoutSession();
      // XDIV 0c: count the checkout surface opening (keepalive ping survives
      // the navigation), then hand the browser to Stripe's hosted payment page.
      pingFunnel("paywall_view");
      window.location.href = checkout_url;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Token expired/invalid server-side -- drop the session and send the
        // user back to the login form rather than a dead Subscribe button.
        logout();
        authChangeCb(false);
        renderLoggedOut();
        onError("Your session expired -- please log in again to subscribe.");
        return;
      }
      statusEl.textContent = `Checkout is unavailable right now: ${describeError(err)}`;
      (button as HTMLButtonElement).disabled = false;
    }
  }

  async function doLogin(email: string, password: string): Promise<void> {
    hideDeviceLimit();
    try {
      await login({ email, password });
      authChangeCb(true);
      const status = await checkEntitlement();
      renderLoggedIn(status);
    } catch (err) {
      // SW-3: the device-cap 409 gets its own in-form message + upsell
      // capture instead of the transient error banner.
      const limit = deviceLimitInfo(err);
      if (limit) {
        showDeviceLimit(email, limit);
        return;
      }
      onError(describeError(err));
    }
  }

  async function doForgotPassword(email: string, statusEl: HTMLElement): Promise<void> {
    const trimmed = email.trim();
    // Ask for the email if the field is empty -- the input is right there,
    // but a prompt is a fine fallback for someone who clicked the link first.
    const target = trimmed || (window.prompt("Enter your account email to reset your password:") ?? "").trim();
    if (!target) return;
    statusEl.textContent = "Sending reset link…";
    try {
      await requestPasswordReset(target);
    } catch (err) {
      // A network/5xx error is the only way this rejects (the backend
      // returns 200 for both known and unknown emails). Show a soft
      // failure without revealing anything about the address.
      statusEl.textContent = `Couldn't send the reset link right now: ${describeError(err)}`;
      return;
    }
    statusEl.textContent =
      "If an account exists for that email, a password reset link is on its way. Check your inbox (and spam).";
  }

  async function doDeleteAccount(): Promise<void> {
    if (!confirm("Delete your account permanently? This cannot be undone.")) return;
    try {
      await deleteAccount();
      authChangeCb(false);
      renderLoggedOut();
    } catch (err) {
      onError(describeError(err));
    }
  }

  if (isLoggedIn()) {
    renderLoggedIn("unknown");
  } else {
    renderLoggedOut();
  }

  return {
    root,
    async refresh(): Promise<EntitlementStatus> {
      if (!isLoggedIn()) {
        renderLoggedOut();
        return "loggedOut";
      }
      const status = await checkEntitlement();
      if (status === "loggedOut") {
        renderLoggedOut();
      } else {
        renderLoggedIn(status);
      }
      return status;
    },
    onAuthChange(cb): void {
      authChangeCb = cb;
    },
  };
}
